import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { areas } from "../data/areas.js";

interface Props {
  areaIndex: number;
  onAreaChange: (index: number) => void;
}

export function AreaSwitcher({ areaIndex, onAreaChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const select = useCallback(
    (i: number) => {
      onAreaChange(i);
      setOpen(false);
    },
    [onAreaChange]
  );

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  return (
    <div
      ref={ref}
      className="fixed z-40 pointer-events-auto"
      style={{
        right: 16,
        bottom: "calc(76px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-14 right-0 min-w-[180px] rounded-2xl overflow-hidden"
            style={{
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(20px) saturate(1.4)",
              WebkitBackdropFilter: "blur(20px) saturate(1.4)",
              boxShadow:
                "0 4px 24px rgba(26,25,23,0.14), 0 1px 4px rgba(26,25,23,0.08)",
            }}
          >
            {areas.map((area, i) => (
              <button
                key={area.id}
                onClick={() => select(i)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  i === areaIndex
                    ? "bg-kra/10"
                    : "hover:bg-surface-1 active:bg-surface-2"
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{
                    background: i === areaIndex ? "#B76E6E" : "#D4D2CE",
                  }}
                />
                <div className="min-w-0">
                  <span
                    className={`text-sm font-medium block ${
                      i === areaIndex ? "text-kra" : "text-ink-0"
                    }`}
                  >
                    {area.name}
                  </span>
                  <span className="text-[11px] text-ink-2">{area.nameJa}</span>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={toggle}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all active:scale-95"
        style={{
          background: open ? "#B76E6E" : "rgba(255,255,255,0.92)",
          backdropFilter: "blur(16px) saturate(1.4)",
          WebkitBackdropFilter: "blur(16px) saturate(1.4)",
          boxShadow:
            "0 2px 16px rgba(26,25,23,0.12), 0 1px 4px rgba(26,25,23,0.08)",
          color: open ? "#fff" : "#1A1917",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="flex-shrink-0"
        >
          <circle cx="8" cy="8" r="3" fill="currentColor" opacity="0.7" />
          <circle
            cx="8"
            cy="8"
            r="6.5"
            stroke="currentColor"
            strokeWidth="1.2"
            opacity="0.5"
          />
        </svg>
        <span className="text-sm font-medium">{areas[areaIndex].name}</span>
      </button>
    </div>
  );
}
